import { createAsyncThunk, createSlice } from "@reduxjs/toolkit";
import { ImgServiceSliceState, ImgSelectType } from "./types";
import { requestServise } from "@/shared";
import { revertAll } from "@/app/model/actions";

const initialState: ImgServiceSliceState = {
  error: null,
  isLoading: false,
  publicBrands: [],
  privateBrands: [],
  designs: [],
  wheelsizes: [],
  lz: [],
  colors: [],
  updatedProductCodes: null,
};

export const fetchImgPubBrands = requestServise.get<ImgSelectType[]>(
  "imageService/fetchImgPubBrands",
  "/image/brands-public",
);

export const fetchImgPrivateBrands = requestServise.get<ImgSelectType[]>(
  "imageService/fetchImgPrivateBrands",
  "/image/brands-private",
);

export const fetchImgDesigns = requestServise.get<ImgSelectType[], string>(
  "imageService/designs",
  "/image/designs",
);

export const fetchImgWheelsizes = requestServise.get<ImgSelectType[], string>(
  "imageService/fetchImgWheelsizes",
  "/image/wheelsizes",
);

export const fetchImgLzs = requestServise.get<ImgSelectType[], string>(
  "imageService/fetchImgLzs",
  "/image/lzs",
);

export const fetchImgColors = requestServise.get<ImgSelectType[], string>(
  "imageService/fetchImgColors",
  "/image/colors",
);

export const submitFormData = createAsyncThunk<
  string[] | null,
  FormData,
  { rejectValue: string }
>("imageService/submitFormData", async (formData, { rejectWithValue }) => {
  try {
    // Для FormData нужно использовать прямой вызов $api, так как requestServise
    // предназначен для JSON запросов
    const $api = await import("@/shared/http").then((m) => m.default);
    const response = await $api.post("/image/bulk-upload", formData, {
      headers: {
        "Content-Type": "multipart/form-data",
      },
    });
    return response.data;
  } catch (error) {
    return rejectWithValue("Ошибка при загрузке данных");
  }
});

export const imageServiceSlice = createSlice({
  name: "imageService",
  initialState,
  reducers: {
    clearUpdatedProductCodes(state) {
      state.updatedProductCodes = null;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(revertAll, () => initialState);

    builder.addCase(fetchImgPubBrands.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(fetchImgPubBrands.fulfilled, (state, action) => {
      state.publicBrands = action.payload;
      state.privateBrands = [];
      state.designs = [];
      state.wheelsizes = [];
      state.lz = [];
      state.colors = [];
      state.isLoading = false;
      state.error = false;
    });
    builder.addCase(fetchImgPubBrands.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchImgPrivateBrands.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(fetchImgPrivateBrands.fulfilled, (state, action) => {
      state.privateBrands = action.payload;
      state.publicBrands = [];
      state.designs = [];
      state.wheelsizes = [];
      state.lz = [];
      state.colors = [];
      state.isLoading = false;
      state.error = false;
    });
    builder.addCase(fetchImgPrivateBrands.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchImgDesigns.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(fetchImgDesigns.fulfilled, (state, action) => {
      state.designs = action.payload;
      state.isLoading = false;
      state.error = false;
    });
    builder.addCase(fetchImgDesigns.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchImgWheelsizes.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(fetchImgWheelsizes.fulfilled, (state, action) => {
      state.wheelsizes = action.payload;
      state.isLoading = false;
      state.error = false;
    });
    builder.addCase(fetchImgWheelsizes.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchImgLzs.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(fetchImgLzs.fulfilled, (state, action) => {
      state.lz = action.payload;
      state.isLoading = false;
      state.error = false;
    });
    builder.addCase(fetchImgLzs.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    builder.addCase(fetchImgColors.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(fetchImgColors.fulfilled, (state, action) => {
      state.colors = action.payload;
      state.isLoading = false;
      state.error = false;
    });
    builder.addCase(fetchImgColors.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
    builder.addCase(submitFormData.pending, (state) => {
      state.error = null;
      state.isLoading = true;
    });
    builder.addCase(submitFormData.fulfilled, (state, action) => {
      state.updatedProductCodes = action.payload;
      state.isLoading = false;
      state.error = null;
    });
    builder.addCase(submitFormData.rejected, (state, action) => {
      state.error = action.payload;
      state.isLoading = false;
    });
  },
});

export const { clearUpdatedProductCodes } = imageServiceSlice.actions;
export default imageServiceSlice;
